import type { ReactElement } from 'react';
import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { CircularProgress } from '@mui/material';
import { Plus } from 'lucide-react';
import { axiosPrivate, axiosPublic } from 'app/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

import type { ServiceItem } from './types';

interface ServiceModalProps {
  open: boolean;
  onClose: () => void;
  onAdd: (item: ServiceItem) => void;
  organisationId: string;
  selectedIds: string[];
}

interface MasterService {
  id: string;
  slug: string;
  name: string;
  shortDescription: string | null;
}

interface MasterServicesPage {
  masterServices: MasterService[];
  pagination: {
    currentPage: number;
    links: {
      next: string | null;
    };
  };
}

async function fetchMasterServices(
  organisationId: string,
  page: number
): Promise<MasterServicesPage> {
  const { data } = await axiosPublic.get<MasterServicesPage>(
    '/v3/service-directory/master-services',
    {
      headers: { 'organisation-id': organisationId },
      params: { page },
    }
  );

  return data;
}

interface MasterServiceDetail {
  id: string;
  name: string;
  iconUrl: string | null;
}

// The endpoint scopes the master service to the organisation through the header, exactly as the
// list call above does.
async function fetchMasterServiceIcon(
  serviceId: string,
  organisationId: string
): Promise<string | undefined> {
  const { data } = await axiosPrivate.get<MasterServiceDetail>(
    `/v3/support/master-services/${serviceId}`,
    { headers: { 'organisation-id': organisationId } }
  );

  return data.iconUrl ?? undefined;
}

export function ServiceModal({
  open,
  onClose,
  onAdd,
  organisationId,
  selectedIds,
}: ServiceModalProps): ReactElement {
  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, isFetching } =
    useInfiniteQuery<MasterServicesPage>({
      queryKey: ['master-services', organisationId],
      queryFn: ({ pageParam = 1 }) => fetchMasterServices(organisationId, pageParam as number),
      getNextPageParam: (lastPage) =>
        lastPage.pagination.links.next ? lastPage.pagination.currentPage + 1 : undefined,
      enabled: open && Boolean(organisationId),
      staleTime: 0,
      meta: { errorMessage: 'Failed to load services' },
    });

  const {
    mutate: addService,
    isPending: isAddingService,
    variables: serviceBeingAdded,
  } = useMutation({
    mutationFn: async (service: ServiceItem): Promise<ServiceItem> => {
      const iconUrl = await fetchMasterServiceIcon(service.id, organisationId);
      return { ...service, iconUrl };
    },
    meta: { errorMessage: 'Failed to load service icon' },
    onSuccess: (item) => {
      onAdd(item);
      onClose();
    },
  });

  const allServices: ServiceItem[] = (data?.pages ?? []).flatMap((page) =>
    page.masterServices.map((s) => ({
      id: s.id,
      name: s.name,
      slug: s.slug,
      description: s.shortDescription ?? undefined,
    }))
  );

  const available = allServices.filter((s) => !selectedIds.includes(s.id));
  const isBlockingFetch = isLoading || (isFetching && !isFetchingNextPage);
  const loadMoreIcon = isFetchingNextPage ? (
    <CircularProgress size={14} className="vtwo:mr-1" />
  ) : null;
  const loadMoreButtonContent = (
    <>
      {loadMoreIcon}
      Load more
    </>
  );

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="vtwo:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add Service</DialogTitle>
          <DialogDescription className="vtwo:sr-only">
            Choose a service to add to this list.
          </DialogDescription>
        </DialogHeader>

        {isBlockingFetch && (
          <div className="vtwo:flex vtwo:justify-center vtwo:py-8">
            <CircularProgress size={24} />
          </div>
        )}

        {!isBlockingFetch && isError && (
          <p className="vtwo:text-sm vtwo:text-red-600 vtwo:py-4 vtwo:text-center">
            Unable to load services. Please try again.
          </p>
        )}

        {!isBlockingFetch && !isError && available.length === 0 && !hasNextPage && (
          <p className="vtwo:text-sm vtwo:text-gray-500 vtwo:py-4 vtwo:text-center">
            No services available
          </p>
        )}

        {!isBlockingFetch && !isError && (available.length > 0 || hasNextPage) && (
          <>
            <ul className="vtwo:divide-y vtwo:divide-gray-100 vtwo:max-h-80 vtwo:overflow-y-auto">
              {available.map((service) => (
                <li
                  key={service.id}
                  className="vtwo:flex vtwo:items-center vtwo:justify-between vtwo:py-3 vtwo:px-1"
                >
                  <span className="vtwo:text-sm vtwo:font-medium vtwo:text-gray-900 vtwo:truncate vtwo:min-w-0 vtwo:mr-4">
                    {service.name}
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="vtwo:shrink-0"
                    disabled={isAddingService}
                    onClick={() => addService(service)}
                  >
                    {isAddingService && serviceBeingAdded?.id === service.id ? (
                      <CircularProgress size={12} className="vtwo:mr-1" />
                    ) : (
                      <Plus className="vtwo:h-3 vtwo:w-3" />
                    )}
                    Add
                  </Button>
                </li>
              ))}
            </ul>

            {hasNextPage && (
              <div className="vtwo:flex vtwo:justify-center vtwo:pt-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fetchNextPage()}
                  disabled={isFetchingNextPage}
                >
                  {loadMoreButtonContent}
                </Button>
              </div>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
